import React, { useState, useEffect } from 'react';
import { PostSendEmail, PostUnsubscribe } from "../API";
import { useCurrentUserData } from "../App";
import { useIntl, FormattedMessage } from "react-intl";
import '../scss/Unsubscribe.scss';
import {Link} from "react-router-dom";
import {ReactComponent as NextArrow} from "../svg/next-arrow.svg";
import {useSnackbar} from "../Content";

const NotificationSettings = () => {
    const intl = useIntl();
    const { currentUserData } = useCurrentUserData();
    const {setSnackbarData} = useSnackbar()
    const [unsubscribeText, setUnsubscribeText] = useState("");
    const [notifications, setNotifications] = useState({
        stats: false,
        immediate: false
    });
    const [previousSettings, setPreviousSettings] = useState({
        stats: false,
        immediate: false
    });
    const [initialSettings, setInitialSettings] = useState({
        stats: false,
        immediate: false
    });
    const [reasonRequired, setReasonRequired] = useState(false);

    const postUnsubscribe = PostUnsubscribe();
    const postSendEmail = PostSendEmail();

    useEffect(() => {
        if (currentUserData?.notifications) {
            const notificationValues = currentUserData.notifications.split(',');
            const newSettings = {
                stats: notificationValues.includes('STATS'),
                immediate: notificationValues.includes('IMMEDIATE')
            };
            setNotifications(newSettings);
            setPreviousSettings(newSettings);
            setInitialSettings(newSettings);
        }
    }, [currentUserData]);

    const getNotificationString = (settings) => {
        const enabledNotifications = [];
        if (settings.stats) enabledNotifications.push('STATS');
        if (settings.immediate) enabledNotifications.push('IMMEDIATE');
        return enabledNotifications.length ? enabledNotifications.join(',') : 'DISABLED';
    };

    const hasSettingsChanged = () => {
        return notifications.stats !== initialSettings.stats ||
            notifications.immediate !== initialSettings.immediate;
    };

    const handleCheckboxChange = (type) => {
        const newSettings = {
            ...notifications,
            [type]: !notifications[type]
        };
        setNotifications(newSettings);

        const hasDisabled = Object.keys(newSettings).some(
            key => newSettings[key] === false && previousSettings[key] === true
        );
        setReasonRequired(hasDisabled);
    };

    const saveSettings = () => {
        const notificationString = getNotificationString(notifications);
        const emailData = {
            subject: `${currentUserData.id} updated notification settings`,
            body: `
        <h1>${currentUserData.id} updated notification settings</h1>
        <p><b>New settings:</b> ${notificationString}</p>
        ${unsubscribeText ? `<p><b>Reason:</b></p><p><em>${unsubscribeText}</em></p>` : ''}
        ${currentUserData.meta ? `
          <table>
            <tbody>
              ${Object.entries(currentUserData.meta)
                .map(([key, value]) => `<tr><th>${key}</th><td>${value}</td></tr>`)
                .join('')}
            </tbody>
          </table>
        ` : ''}`
        };

        postUnsubscribe.mutate({ notifications: notificationString }, {
            onSuccess: () => {
                setPreviousSettings(notifications);
                setInitialSettings(notifications)
                setUnsubscribeText("");
                setReasonRequired(false);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({id:"notificationSettingsUpdated",defaultMessage:"Notification settings updated!"}),
                    state: "success",
                    autoHide: true,
                });

                postSendEmail.mutate(emailData, {
                    onError: (error) => {
                        console.log('Failed to send email:', error)
                    }
                });
            },
            onError: error => {
                console.log(error)
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({id:"saveFailed",defaultMessage:"Save failed. Please try again."}),
                    state: "error",
                    autoHide: true,
                })
            },
        });
    };

    const loading = postUnsubscribe.isLoading || postSendEmail.isLoading;
    const isSubmitDisabled = !hasSettingsChanged() ||
        (reasonRequired && unsubscribeText.length < 1) ||
        loading;

    return <div className={"unsubscribe-container"}>
        <div className={"unsubscribe"}>
            <h1>
                {intl.formatMessage({
                    id: 'emailNotifications',
                    defaultMessage: 'Email notifications',
                })}
            </h1>
            <div className={"checkboxes"}>
                <label>
                    <input
                        type="checkbox"
                        checked={notifications.immediate}
                        onChange={() => handleCheckboxChange('immediate')}
                        disabled={loading}
                    />
                    {intl.formatMessage({
                        id: 'notifications.IMMEDIATE',
                        defaultMessage: 'Support campaigns',
                    })}
                </label>
                <p>
                    {intl.formatMessage({
                        defaultMessage: "Receive all emails related to support campaigns, like notifications of new support requests.",
                        id: "notifications.description.IMMEDIATE"
                    })}
                </p>
                <hr/>
                <label>
                    <input
                        type="checkbox"
                        checked={notifications.stats}
                        onChange={() => handleCheckboxChange('stats')}
                        disabled={loading}
                    />
                    {intl.formatMessage({
                        id: 'notifications.STATS',
                        defaultMessage: 'Statistics',
                    })}
                </label>
                <p>
                    {intl.formatMessage({
                        defaultMessage: "Receive a quarterly email about your institutions Annie statistics.",
                        id: "notifications.description.STATS"
                    })}
                </p>
                <hr/>
            </div>

            {reasonRequired && (
                <textarea
                    value={unsubscribeText}
                    disabled={loading}
                    onChange={(e) => setUnsubscribeText(e.target.value)}
                    placeholder={intl.formatMessage({
                        id: 'unsubscribePlaceholder',
                        defaultMessage: "Please describe why you're disabling these notifications.",
                    })}
                />
            )}

            <button
                onClick={saveSettings}
                disabled={isSubmitDisabled || loading}
                className={`main secondary${isSubmitDisabled || loading ? ' disabled' : ''}`}
            >
                {loading ?
                    intl.formatMessage({
                        id: 'saving',
                        defaultMessage: 'Saving...',
                    }) :
                    intl.formatMessage({
                        id: 'saveNotificationSettings',
                        defaultMessage: 'Save Notification Settings',
                    })
                }
            </button>
        </div>
        <Link to={"/"} state={{animation:"true",direction:'left'}} className={"button tertiary main"}>
            {intl.formatMessage({
                id: 'allSupportRequests',
                defaultMessage: 'All support requests',
            })}
            <NextArrow />
        </Link>
    </div>
};

export default NotificationSettings;